import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Mail } from "lucide-react";
import { api, extractError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { AnthropicKeyCard } from "@/components/AnthropicKeyCard";
import { PromptCard } from "@/components/PromptCard";
import { NotionCard } from "@/components/NotionCard";

function GoogleCard({ it, reload }) {
    const [busy, setBusy] = useState(false);
    const connect = async () => {
        setBusy(true);
        try { const { data } = await api.post("/integrations/google/start"); window.location.href = data.authorization_url; }
        catch (e) { toast.error(extractError(e)); setBusy(false); }
    };
    const toggle = async (v) => {
        try { await api.patch(`/integrations/${it.kind}`, { enabled: v }); reload(); } catch (e) { toast.error(extractError(e)); }
    };
    const disconnect = async () => {
        if (!window.confirm("Scollegare l'account Google dello studio? Verranno disattivati sia Google Calendar sia Gmail.")) return;
        try { await api.delete(`/integrations/oauth/${it.kind}`); reload(); } catch (e) { toast.error(extractError(e)); }
    };
    return (
        <div className="bg-white border border-neutral-200 rounded-md p-5 flex items-start gap-4" data-testid={`integration-${it.kind}`}>
            {it.kind === "gmail" ? <Mail size={20} className="text-neutral-500 mt-0.5" /> : <CalendarDays size={20} className="text-neutral-500 mt-0.5" />}
            <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{it.label}</div>
                <div className="text-xs text-neutral-500 mt-0.5" data-testid={`integration-${it.kind}-status`}>
                    {it.connected ? `Collegato a ${it.account_email} · sola lettura`
                        : it.account_connected ? `Account ${it.account_email} collegato, ma senza permesso per ${it.label}: ricollega e concedi l'accesso.`
                        : `Non collegato. La Time Machine funziona comunque, senza i dati di ${it.label}.`}
                </div>
                {it.last_error && <div className="text-xs text-red-600 mt-1" data-testid={`integration-${it.kind}-error`}>Ultimo errore: {it.last_error}</div>}
            </div>
            {it.connected ? (
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-xs text-neutral-600">
                        <Switch checked={it.enabled} onCheckedChange={toggle} data-testid={`integration-${it.kind}-toggle`} /> {it.enabled ? "Attivo" : "Disattivato"}
                    </label>
                    <button onClick={disconnect} data-testid={`integration-${it.kind}-disconnect`} className="text-xs text-neutral-500 hover:text-red-600">Scollega</button>
                </div>
            ) : (
                <button onClick={connect} disabled={busy} data-testid={`integration-${it.kind}-connect`}
                    className="text-xs px-3 py-1.5 rounded-md bg-neutral-900 text-white disabled:opacity-50">{busy ? "Apertura Google…" : it.account_connected ? "Ricollega e concedi accesso" : "Collega account Google"}</button>
            )}
        </div>
    );
}

export function IntegrationsEditor() {
    const [items, setItems] = useState([]);
    const [params, setParams] = useSearchParams();
    const load = async () => { try { setItems((await api.get("/integrations")).data); } catch (e) { toast.error(extractError(e)); } };
    useEffect(() => {
        load();
        const g = params.get("google");
        if (g === "ok") toast.success("Account Google collegato");
        if (g === "error") toast.error("Collegamento Google non riuscito o annullato");
        if (g === "missing_scope") toast.error("Non hai concesso l'accesso al calendario: ricollega e spunta le caselle di calendario e posta", { duration: 12000 });
        if (g) { params.delete("google"); setParams(params, { replace: true }); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <div className="space-y-3 max-w-3xl" data-testid="integrations-editor">
            <div className="text-sm text-neutral-500">Account esterni dello studio usati dalla Time Machine. Ogni integrazione si attiva e disattiva singolarmente.</div>
            {items.map((it) => <GoogleCard key={it.kind} it={it} reload={load} />)}
            <NotionCard />
            <AnthropicKeyCard />
            <PromptCard />
        </div>
    );
}
